import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import * as signalR from '@microsoft/signalr';
import axios from 'axios';

import { Input, Modal, Table } from '@/common';
import { ErrorPage } from '@/components/common/ErrorPage';
import { Button, Label, LABEL_TYPE } from '@/components/generic';
import {
  BUTTON_TYPE,
  ButtonConfig,
  ButtonVariant
} from '@/components/generic/Button/button-config';
import { IconButtonVariant } from '@/components/generic/IconButton/icon-button-config';
import { DetailsPageLayout } from '@/components/layouts';
import { ConfirmationModal } from '@/components/modals/ConfirmationModal';
import { queryClient } from '@/configuration/react-query';
import { dateAndHoursFormat, PAGE_SIZE } from '@/constants';
import { AuthContext } from '@/contexts';
import { DeviceTabs, ORGANIZATION_PERMISSION_TYPE, PERMISSION_TYPE } from '@/enums';
import {
  apiUrl,
  getDeviceBaseUrl,
  getDeviceEventsColumns,
  getDeviceScreensColumns,
  organizationHasPermission,
  webSocketApiUrl
} from '@/helpers';
import { formatDate } from '@/helpers/date';
import { showToast, ToastType } from '@/helpers/toast';
import { EVENTS_PATH, EVENTS_REFETCH_INTERVAL } from '@/pages/Events';
import { Devices as DeviceIcon, EditPenIcon, PlusIcons, TrashIcon } from '@/resources';
import {
  useCreateScreenMutation,
  useDeleteDeviceMutation,
  useDeleteScreenMutation,
  useGetAllEvents,
  useGetAllScreens,
  useGetDeviceQuery,
  useGetOrganizationsByIdsSimplifiedQueries,
  useUpdateScreenNameMutation
} from '@/services';
import {
  AllowedPermissions,
  Event,
  Organization,
  OrganizationBasic,
  Screen,
  TableData
} from '@/types';

export const DeviceDetails = () => {
  const [t] = useTranslation('common');
  const { deviceId } = useParams();
  const { permissions, hasOrganizationPermission } = useContext(AuthContext);
  const navigate = useNavigate();
  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState<boolean>(false);
  const [screenName, setScreenName] = useState('');
  const [screenModal, setScreenModal] = useState<{ isOpen: boolean; screen?: Screen }>({
    isOpen: false
  });
  const [isOpenDeleteScreenModal, setIsOpenDeleteScreenModal] = useState<boolean>(false);
  const [selectedScreen, setSelectedScreen] = useState<Screen | null>(null);
  const [error, setError] = useState<number>();
  const [controllerUrl, setControllerUrl] = useState('');
  const [currentTab, setCurrentTab] = useState<string>(DeviceTabs.CEREMONIES);
  const hasSetInitialTab = useRef(false);

  const allowedPermissions: AllowedPermissions = {
    editPermission: PERMISSION_TYPE.UPDATE_DEVICE,
    deletePermission: PERMISSION_TYPE.DELETE_DEVICE
  };

  const allowedScreensPermissions: AllowedPermissions = {
    createPermission: PERMISSION_TYPE.CREATE_DEVICE,
    editPermission: PERMISSION_TYPE.UPDATE_DEVICE,
    deletePermission: PERMISSION_TYPE.DELETE_DEVICE
  };
  const {
    data: device,
    isLoading: isDeviceLoading,
    refetch: refetchDevice,
    isError: isDeviceError
  } = useGetDeviceQuery(Number(deviceId), {
    onSuccess: () => {
      undefined;
    },
    onError: (error) => {
      setError(error.status);
    }
  });

  // The tabs, the screens tab and adding screens are only for a device in at least one
  // organization with digital signage (the device can belong to several).
  const deviceOrganizations = useGetOrganizationsByIdsSimplifiedQueries(
    device?.data?.organizations?.map((organization: OrganizationBasic) => organization.id) ?? []
  );
  const hasDigitalSignage =
    hasOrganizationPermission(ORGANIZATION_PERMISSION_TYPE.DIGITAL_SIGNAGE) &&
    deviceOrganizations.some(
      (organization) =>
        !!organization.data?.data &&
        organizationHasPermission(
          organization.data.data,
          ORGANIZATION_PERMISSION_TYPE.DIGITAL_SIGNAGE
        )
    );
  const activeTab = hasDigitalSignage ? currentTab : DeviceTabs.CEREMONIES;

  const {
    data: allEvents,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    refetch: refetchAllEvents
  } = useGetAllEvents(
    PAGE_SIZE,
    undefined,
    'createdAt',
    false,
    undefined,
    undefined,
    !!device && permissions.includes(PERMISSION_TYPE.VIEW_EVENTS),
    Number(deviceId)
  );

  const {
    data: allScreens,
    isFetchingNextPage: isFetchingNextPageScreens,
    fetchNextPage: fetchNextPageScreens,
    hasNextPage: hasNextPageScreens
  } = useGetAllScreens(PAGE_SIZE, undefined, Number(deviceId), hasDigitalSignage);

  const { mutate: createScreen, isLoading: isCreatingScreen } = useCreateScreenMutation(
    queryClient,
    {
      onSuccess: () => {
        setScreenName('');
        setScreenModal({ isOpen: false });
        showToast(ToastType.SUCCESS, t('screenCreated'));
      },
      onError: (error) => {
        showToast(ToastType.ERROR, error.response.data.ErrorMessages[0].DisplayMessage);
      }
    }
  );

  const { mutate: updateScreen, isLoading: isUpdatingScreen } = useUpdateScreenNameMutation(
    queryClient,
    {
      onSuccess: () => {
        setScreenName('');
        setScreenModal({ isOpen: false });
        showToast(ToastType.SUCCESS, t('screenUpdated'));
      },
      onError: (error) => {
        showToast(ToastType.ERROR, error.response.data.ErrorMessages[0].DisplayMessage);
      }
    }
  );

  const { mutate: deleteScreen } = useDeleteScreenMutation(queryClient, {
    onSuccess: () => {
      setIsOpenDeleteScreenModal(false);
      setSelectedScreen(null);
      showToast(ToastType.SUCCESS, t('screenDeleted'));
    },
    onError: (error) => {
      setIsOpenDeleteScreenModal(false);
      showToast(ToastType.ERROR, error.response.data.ErrorMessages[0].DisplayMessage);
    }
  });

  const { mutate: deleteDevice } = useDeleteDeviceMutation(queryClient, {
    onSuccess: () => {
      navigate('/devices');
      setIsOpenDeleteModal(false);
    },
    onError: () => {
      setIsOpenDeleteModal(false);
    }
  });

  const getDeviceUrl = async () => {
    const baseUrl = getDeviceBaseUrl(device?.data?.localIP, device?.data?.serverPort);
    if (!baseUrl) {
      setControllerUrl('');
      return;
    }

    const response = await axios.get(`${apiUrl()}/v1/user/generate_token`);
    const token = response?.data;
    if (token) {
      setControllerUrl(`${baseUrl}/?token=${token}`);
    } else {
      console.error('Token not available');
    }
  };

  useEffect(() => {
    const connection = new signalR.HubConnectionBuilder()
      .withUrl(`${webSocketApiUrl()}/notificationHub`, {
        skipNegotiation: true,
        transport: signalR.HttpTransportType.WebSockets
      })
      .build();

    connection.on('deviceUpdate', () => {
      refetchDevice();
    });

    const startPromise = connection.start().catch(err => console.log('err', err));

    return () => {
      connection.off('deviceUpdate', refetchDevice);
      startPromise.finally(() => {
        connection.stop().catch(err => console.log('err', err));
      });
    };
  }, []);

  useEffect(() => {
    if (device) {
      getDeviceUrl();
    }
  }, [device]);

  const handleEditDevice = () => {
    navigate(`/devices/${deviceId}/edit`);
  };

  const handleDelete = () => {
    setIsOpenDeleteModal(true);
  };

  const onCloseDeleteModal = () => {
    setIsOpenDeleteModal(false);
  };

  useEffect(() => {
    if (hasSetInitialTab.current) {
      return;
    }
    if (allEvents === undefined || allScreens === undefined) {
      return;
    }

    hasSetInitialTab.current = true;

    const hasEvents = allEvents.pages.flatMap((p) => p.data?.items).length > 0;
    const hasScreens = allScreens.pages.flatMap((p) => p.data?.items).length > 0;

    if (hasEvents) {
      setCurrentTab(DeviceTabs.CEREMONIES);
    } else if (hasScreens) {
      setCurrentTab(DeviceTabs.SCREENS);
    }
  }, [allEvents, allScreens]);

  const screensBaseUrl = getDeviceBaseUrl(device?.data?.localIP, device?.data?.serverPort);

  const formatScreensData = useMemo(() => {
    return allScreens?.pages.flatMap((page) =>
      page.data?.items.map((screen: Screen) => ({
        ...screen,
        enabled: true,
        playerLink: screensBaseUrl ? `${screensBaseUrl}/player/${screen.token}` : null
      }))
    ) as TableData[];
  }, [allScreens, screensBaseUrl]);

  const formatData = useMemo(() => {
    return allEvents?.pages.flatMap((page) =>
      page.data?.items.map((eventData: Event) => ({
        ...eventData,
        date: formatDate(new Date(eventData.startDateAndTime)),
        organization: eventData.organization?.name
      }))
    ) as TableData[];
  }, [allEvents, dateAndHoursFormat]);

  useEffect(() => {
    if (isDeviceError) {
      return;
    }

    const intervalId = setInterval(() => {
      if (!document.hidden) {
        refetchDevice();
        if (!!device && permissions.includes(PERMISSION_TYPE.VIEW_EVENTS)) {
          refetchAllEvents();
        }
      }
    }, EVENTS_REFETCH_INTERVAL);

    return () => clearInterval(intervalId);
  }, [refetchAllEvents, refetchDevice, isDeviceError]);

  const isDefaultDate = useMemo(() => {
    const parsedDate = device?.data?.lastSeen ? new Date(device?.data?.lastSeen) : null;

    if (parsedDate && !isNaN(parsedDate.getTime())) {
      return parsedDate.getUTCFullYear() === 0;
    }

    return false;
  }, [device?.data?.lastSeen]);

  const isValidLastSeen = device?.data?.lastSeen && !isDefaultDate;

  const mobileHeaderButtonConfig: ButtonConfig[] = [
    {
      label: t('goToController'),
      type: BUTTON_TYPE.LINK,
      path: controllerUrl,
      className: 'lg:px-6 mb-2.5 px-2 w-full',
      permissions: []
    }
  ];

  const headerButtonConfig: ButtonConfig[] = [
    {
      label: t('goToController'),
      type: BUTTON_TYPE.LINK,
      path: controllerUrl,
      className: 'hidden md:block',
      permissions: []
    },
    {
      icon: <EditPenIcon className='text-primary-blue' />,
      variant: IconButtonVariant.PRIMARY,
      type: BUTTON_TYPE.ICON_BUTTON,
      handleOnClick: handleEditDevice,
      permissions: [PERMISSION_TYPE.UPDATE_DEVICE]
    },
    {
      icon: <TrashIcon className='h-4 w-3 md:h-5 md:w-4' />,
      variant: IconButtonVariant.PRIMARY,
      type: BUTTON_TYPE.ICON_BUTTON,
      handleOnClick: handleDelete,
      permissions: [PERMISSION_TYPE.DELETE_DEVICE]
    }
  ];

  return error ? (
    <ErrorPage code={error} />
  ) : (
    <div>
      <DetailsPageLayout
        icon={<DeviceIcon className='text-primary-blue' />}
        iconPath={'/devices'}
        path={[{ label: t('deviceDetails') }]}
        title={t(device?.data?.name ?? 'deviceDetails')}
        tag={device?.data.status}
        statusDescription={device?.data.statusDescription}
        isLoading={isDeviceLoading}
        headerButtons={headerButtonConfig}
        mobileHeaderButtons={mobileHeaderButtonConfig}>
        {
          <>
            <div className='mt-3 flex md:mt-12'>
              <div className='flex w-full flex-col gap-4'>
                <div className='flex w-full flex-col md:flex-row md:items-center'>
                  <Label
                    text={`${t('description')}:`}
                    type={LABEL_TYPE.PARAGRAPH1}
                    className='!md:text-secondary-gray pb-2.5 sm:p-0 md:w-1/6 md:pb-0'
                  />
                  <Label
                    text={device?.data?.description ?? ''}
                    type={LABEL_TYPE.PARAGRAPH1}
                    className='!text-primary-black'
                  />
                  {device?.data?.description === '' && <span className='invisible'>&nbsp;</span>}
                </div>
                <div className='flex w-full flex-col md:flex-row md:items-center'>
                  <Label
                    text={`${t('hardwareId')}:`}
                    type={LABEL_TYPE.PARAGRAPH1}
                    className='pb-2.5 text-secondary-gray sm:p-0 md:w-1/6'
                  />
                  <Label
                    text={device?.data?.hardwareId ?? ''}
                    type={LABEL_TYPE.PARAGRAPH1}
                    className='!text-primary-black'
                  />
                </div>
                <div className='flex w-full flex-col md:flex-row md:items-center'>
                  <Label
                    text={`${t('organizations')}:`}
                    type={LABEL_TYPE.PARAGRAPH1}
                    className='pb-2.5 text-secondary-gray sm:p-0 md:w-1/6'
                  />
                  <Label
                    text={device?.data.organizations
                      .map((org: Organization) => org.name)
                      .join(', ')}
                    type={LABEL_TYPE.PARAGRAPH1}
                    className='!text-primary-black'
                  />
                </div>
                <div className='flex w-full flex-col md:flex-row md:items-center'>
                  <Label
                    text={`${t('availability')}:`}
                    type={LABEL_TYPE.PARAGRAPH1}
                    className='pb-2.5 text-secondary-gray sm:p-0 md:w-1/6'
                  />
                  <Label
                    text={device?.data?.enabled ? t('enabled') : t('disabled')}
                    type={LABEL_TYPE.PARAGRAPH1}
                    className='!text-primary-black'
                  />
                </div>
                <div className='flex w-full flex-col md:flex-row md:items-center'>
                  <Label
                    text={`${t('localIp')}:`}
                    type={LABEL_TYPE.PARAGRAPH1}
                    className='pb-2.5 text-secondary-gray sm:p-0 md:w-1/6'
                  />
                  <Label
                    text={device?.data?.localIP ?? ''}
                    type={LABEL_TYPE.PARAGRAPH1}
                    className='!text-primary-black'
                  />
                  {device?.data?.localIP === '' && <span className='invisible'>&nbsp;</span>}
                </div>
                <div className='flex w-full flex-col md:flex-row md:items-center'>
                  <Label
                    text={`${t('streamId')}:`}
                    type={LABEL_TYPE.PARAGRAPH1}
                    className='pb-2.5 text-secondary-gray sm:p-0 md:w-1/6'
                  />
                  <Label
                    text={device?.data?.streamId ?? ''}
                    type={LABEL_TYPE.PARAGRAPH1}
                    className='!text-primary-black'
                  />
                </div>
                <div className='flex w-full flex-col md:flex-row md:items-center'>
                  <Label
                    text={`${t('hardwareInfo')}:`}
                    type={LABEL_TYPE.PARAGRAPH1}
                    className='!md:text-secondary-gray pb-2.5 sm:p-0 md:w-1/6 md:pb-0'
                  />
                  <Label
                    text={device?.data?.hardwareInfo ?? ''}
                    type={LABEL_TYPE.PARAGRAPH1}
                    className='!text-primary-black'
                  />
                  {device?.data?.hardwareInfo === '' && <span className='invisible'>&nbsp;</span>}
                </div>
                {device?.data.playerRelease && (
                  <div className='flex w-full flex-col md:flex-row md:items-center'>
                    <Label
                      text={`${t('playerVersion')}:`}
                      type={LABEL_TYPE.PARAGRAPH1}
                      className='pb-2.5 text-secondary-gray sm:p-0 md:w-1/6'
                    />
                    <Label
                      text={device?.data.playerRelease.version ?? ''}
                      type={LABEL_TYPE.PARAGRAPH1}
                      className='!text-primary-black'
                    />
                    {device?.data?.localIP === '' && <span className='invisible'>&nbsp;</span>}
                  </div>
                )}
                <div className='flex w-full flex-col md:flex-row md:items-center'>
                  <Label
                    text={`${t('lastSeen')}:`}
                    type={LABEL_TYPE.PARAGRAPH1}
                    className='pb-2.5 text-secondary-gray sm:p-0 md:w-1/6'
                  />
                  <Label
                    text={isValidLastSeen ? formatDate(new Date(device.data.lastSeen)) : t('never')}
                    type={LABEL_TYPE.PARAGRAPH1}
                    className='!text-primary-black'
                  />
                </div>
              </div>
            </div>
          </>
        }

        {hasDigitalSignage && (
          <>
            <div
              className={
                'no-scrollbar mt-4 flex flex-row overflow-x-scroll md:mt-8 lg:overflow-x-hidden'
              }>
              {Object.values(DeviceTabs).map((tab) => (
                <div
                  key={tab}
                  className={`cursor-pointer whitespace-nowrap rounded px-5 py-2.5 ${
                    activeTab === tab && 'bg-secondary-pale-blue-gray font-bold'
                  }`}
                  onClick={() => {
                    setCurrentTab(tab);
                  }}>
                  {t(tab)}
                </div>
              ))}
            </div>
            <div className='w-full border-b border-secondary-blue-gray' />
          </>
        )}
        {activeTab === DeviceTabs.CEREMONIES && formatData && formatData?.length > 0 && (
          <div className='mt-5'>
            <Table
              data={formatData}
              columns={getDeviceEventsColumns()}
              permissions={allowedPermissions}
              handleViewDetails={(item) =>
                permissions.includes(PERMISSION_TYPE.GET_EVENT)
                  ? navigate(EVENTS_PATH + item.id)
                  : undefined
              }
              isFetchingNextPage={isFetchingNextPage}
              fetchNextPage={fetchNextPage}
              hasNextPage={hasNextPage}
              containerClassName='max-h-[25vh] 3xl:max-h-[40vh] px-2 md:px-0 4xl:max-h-[40vh]'
              numberOfAppliedFilters={0}
              hiddenColumnsMobile={[]}
            />
          </div>
        )}
        {activeTab === DeviceTabs.SCREENS && (
          <div className='mt-5'>
            <div className='mb-4 flex items-center justify-end'>
              <Button
                variant={ButtonVariant.PRIMARY}
                handleOnClick={() => {
                  setScreenName('');
                  setScreenModal({ isOpen: true });
                }}
                disabled={!permissions.includes(PERMISSION_TYPE.CREATE_DEVICE)}>
                <PlusIcons className='size-3.5 md:mr-2' fill='white' />
                <div className='hidden md:block'>{t('addScreen')}</div>
              </Button>
            </div>
            {formatScreensData && formatScreensData?.length > 0 && (
              <Table
                data={formatScreensData}
                columns={getDeviceScreensColumns()}
                permissions={allowedScreensPermissions}
                isFetchingNextPage={isFetchingNextPageScreens}
                fetchNextPage={fetchNextPageScreens}
                hasNextPage={hasNextPageScreens}
                containerClassName='max-h-[25vh] 3xl:max-h-[40vh] px-2 md:px-0 4xl:max-h-[40vh]'
                numberOfAppliedFilters={0}
                hiddenColumnsMobile={[]}
                handleEdit={(item) => {
                  const screen = item as unknown as Screen;
                  setScreenName(screen.name);
                  setScreenModal({ isOpen: true, screen });
                }}
                handleDelete={(item) => {
                  setSelectedScreen(item as unknown as Screen);
                  setIsOpenDeleteScreenModal(true);
                }}
              />
            )}
          </div>
        )}
      </DetailsPageLayout>
      {isOpenDeleteModal && (
        <ConfirmationModal
          onConfirm={() => deleteDevice(device?.data?.id)}
          isOpen={isOpenDeleteModal}
          onClose={onCloseDeleteModal}
          message={t('deleteMessage', { item: device?.data?.name })}
        />
      )}
      <Modal
        isOpen={screenModal.isOpen}
        onClose={() => {
          setScreenName('');
          setScreenModal({ isOpen: false });
        }}
        label={screenModal.screen ? 'editScreen' : 'addScreen'}
        hasButton
        hasCancelButton
        buttonText={screenModal.screen ? t('edit') : t('add')}
        isButtonDisabled={!screenName.trim() || isCreatingScreen || isUpdatingScreen}
        handleSubmit={() => {
          if (screenModal.screen) {
            updateScreen({ id: screenModal.screen.id, name: screenName.trim() });
          } else {
            createScreen({ name: screenName.trim(), deviceId: Number(deviceId) });
          }
        }}
        modalClasses='w-full md:w-[600px] h-[300px] h-auto'
        containerClasses='flex flex-col gap-5 py-2'
        height='h-full'
        buttonClasses='w-full flex justify-end'>
        <Input
          id='screenName'
          label={t('name')}
          type='text'
          value={screenName}
          handleChange={setScreenName}
          isRequired
        />
      </Modal>
      {isOpenDeleteScreenModal && selectedScreen && (
        <ConfirmationModal
          onConfirm={() => deleteScreen(selectedScreen.id)}
          isOpen={isOpenDeleteScreenModal}
          onClose={() => {
            setIsOpenDeleteScreenModal(false);
            setSelectedScreen(null);
          }}
          message={t('deleteMessage', { item: selectedScreen.name })}
        />
      )}
    </div>
  );
};
